import Cookies from 'universal-cookie';
import ObexRequestHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import RequestHandler from '../handlers/RequestHandler';
import { urlHttp, urlLocal, useLocalBackend, apiKeyP80 } from '../configs/appConfig.json';
import { Cashier } from '../types/cashier.types';
import { getTerminalToken, setWarehouseId, clearTerminalToken } from './TerminalService';
import { buildQuery, unwrapList, unwrapObject, getErrorMessage } from './apiUtils';

/**
 * Nombres publicados en el gateway, que no siguen ninguna regla deducible desde
 * el registerEndpoint del .avap. Verificado contra produccion:
 * /terminals-available-cashiers responde, y /available-cashiers (lo que salia de
 * aplanar la ruta con toGatewayPath) da "Service not Available".
 */
const ROUTES = {
    loginPin: '/auth/login-pin'
};

/**
 * available-cashiers no encaja en el aplanado de ObexRequestHandlerP80: el
 * gateway lo publica plano y con guion (/terminals-available-cashiers; el
 * toGatewayPath daria /available-cashiers, que responde "Service not
 * Available"), mientras que el servidor local lo sirve con la ruta del
 * registerEndpoint (/terminals/available-cashiers). Verificado contra los dos.
 * Por eso el destino se elige aqui a mano en vez de pasar por el handler.
 */
const CASHIERS_URL = useLocalBackend
    ? `${urlLocal}/terminals/available-cashiers`
    : `${urlHttp}/terminals-available-cashiers`;

/** El web_token caduca a los 3600s (getDateTime 3600 en login-pin.avap). */
const WEB_TOKEN_TTL_SECONDS = 3600;

export const TERMINAL_NOT_PROVISIONED =
    'Terminal no dado de alta. Contacta con administracion para activar este TPV.';

export const TERMINAL_NO_WAREHOUSE =
    'Este terminal no tiene almacén asignado. Contacta con administración para asignárselo.';

export const mapCashier = (raw: any): Cashier => ({
    id:           String(raw.id),
    name:         raw.name || '',
    lastName:     raw.last_name || '',
    username:     raw.username || '',
    avatarBase64: raw.avatar_base64 || undefined
});

const fetchCashiers = async (terminalToken: string): Promise<Cashier[]> => {
    const res = await RequestHandler.get(
        `${CASHIERS_URL}${buildQuery({ terminal_token: terminalToken })}`,
        {},
        { '101obexapikey': apiKeyP80, 'disable-cache': false }
    );

    const error = getErrorMessage(res);
    if (error) throw new Error(error);

    return unwrapList(res).map(mapCashier);
};

/** Cajeros activos con rol 'cajero'. El filtrado lo hace el backend. */
export const listAvailableCashiers = async (): Promise<Cashier[]> => {
    const terminalToken = getTerminalToken();
    if (!terminalToken) throw new Error(TERMINAL_NOT_PROVISIONED);
    try {
        return await fetchCashiers(terminalToken);
    } catch (err) {
        // El codigo guardado no conecta: se descarta y TerminalGate lo pide de nuevo.
        clearTerminalToken();
        throw err;
    }
};

/**
 * Comprueba un codigo de terminal ANTES de guardarlo, con la misma llamada que
 * hara la pantalla de cajeros: si el backend lo reconoce, responde la lista.
 */
export const validateTerminalToken = async (terminalToken: string): Promise<void> => {
    try {
        await fetchCashiers(terminalToken);
    } catch (err) {
        throw new Error(`Código de terminal no válido: ${(err as any)?.message || 'sin respuesta del servidor'}`);
    }
};

/**
 * user_id no esta en login-pin.md pero el .avap desplegado lo acepta y acota
 * la comprobacion del PIN a ese usuario. Es lo que da sentido al selector.
 */
export const loginWithPin = async (userId: string, pin: string): Promise<Cashier> => {
    const terminalToken = getTerminalToken();
    if (!terminalToken) throw new Error(TERMINAL_NOT_PROVISIONED);

    const res = await ObexRequestHandlerP80.get(
        `${ROUTES.loginPin}${buildQuery({
            terminal_token: terminalToken,
            user_id:        userId,
            pin:            pin
        })}`
    );

    const error = getErrorMessage(res);
    if (error) throw new Error(error);

    const data = unwrapObject(res);
    if (!data || !data.web_token) throw new Error('Respuesta de login incompleta.');

    // OJO: login-pin.avap serializa mal el id (AddvariableToJSON mete el valor
    // como clave), asi que data.user_id llega undefined. Usamos el userId que ya
    // nos pasaron: es el cajero seleccionado, contra el que se valido el PIN.
    const cashier: Cashier = {
        id:           userId,
        name:         data.name || '',
        lastName:     data.last_name || '',
        username:     '',
        avatarBase64: data.avatar_base64 || undefined
    };

    // El almacen del terminal (pos_terminals.warehouse_id) viaja en la respuesta
    // de login-pin. Se guarda aparte de la cookie porque no es de la sesion del
    // cajero sino de la maquina.
    //
    // Sin almacen el TPV no entra: books_list no filtra y se venderia contra el
    // catalogo y el stock de todos los almacenes. Se borra tambien el almacen
    // que pudiera quedar de otro terminal en este navegador, y se corta antes de
    // crear la cookie de sesion.
    if (!data.warehouse_id) {
        setWarehouseId('');
        throw new Error(TERMINAL_NO_WAREHOUSE);
    }
    setWarehouseId(String(data.warehouse_id));

    // Cookie '101Obex': convencion documentada en avap-react-bridge/SKILL.md.
    // expiresAt permite descartar el token caducado antes de llamar.
    new Cookies().set(
        '101Obex',
        {
            token:        data.web_token,
            refreshToken: data.refresh_token,
            expiresAt:    Date.now() + WEB_TOKEN_TTL_SECONDS * 1000,
            terminalId:   String(data.terminal_id),
            user:         cashier
        },
        { path: '/', maxAge: WEB_TOKEN_TTL_SECONDS, sameSite: 'strict' }
    );

    return cashier;
};

export const logout = (): void => {
    new Cookies().remove('101Obex', { path: '/' });
};
